import { AxiosError } from 'axios';
import i18next from 'i18next';
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { providerTypesApi } from 'api';
import { QueryKey } from 'enums';
import {
  CreateProviderTypeDto,
  ProviderType,
  UpdateProviderTypeDto,
} from 'types';

import { useMutation, usePartialQuery } from '.';

export const useProviderTypes = () => {
  const queryClient = useQueryClient();

  const queryResult = usePartialQuery(
    QueryKey.ProviderTypes,
    providerTypesApi.getAllPaginated,
  );

  const allActiveQuery = useQuery<ProviderType[], AxiosError>({
    queryKey: [QueryKey.ProviderTypes, 'all-active'],
    queryFn: providerTypesApi.getAllActive,
  });

  const allQuery = useQuery<ProviderType[], AxiosError>({
    queryKey: [QueryKey.ProviderTypes, 'all'],
    queryFn: providerTypesApi.getAllIncludingInactive,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.ProviderTypes] });
  }, [queryClient]);

  const createMutation = useMutation<
    ProviderType,
    AxiosError,
    CreateProviderTypeDto
  >(providerTypesApi.create, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const updateMutation = useMutation<
    ProviderType,
    AxiosError,
    { id: string; data: UpdateProviderTypeDto }
  >(providerTypesApi.update, {
    notifierType: 'save',
    onSuccess: invalidateQuery,
  });

  const removeMutation = useMutation<void, AxiosError, string>(
    providerTypesApi.remove,
    {
      notifierType: 'remove',
      onSuccess: invalidateQuery,
    },
  );

  const activateMutation = useMutation<ProviderType, AxiosError, string>(
    providerTypesApi.activate,
    {
      notifierType: 'save',
      onSuccess: invalidateQuery,
    },
  );

  const deactivateMutation = useMutation<ProviderType, AxiosError, string>(
    providerTypesApi.deactivate,
    {
      notifierType: 'save',
      onSuccess: invalidateQuery,
    },
  );

  const providerTypeOptions = useMemo(() => {
    const types = allActiveQuery.data || [];
    return types.map((type: ProviderType) => ({
      value: type.code,
      label: type.isSystem
        ? i18next.t(`p2p_providers.${type.code}`, { defaultValue: type.name })
        : type.name,
      isSystem: type.isSystem,
    }));
  }, [allActiveQuery.data]);

  const systemProviderTypeOptions = useMemo(
    () => providerTypeOptions.filter((opt) => opt.isSystem),
    [providerTypeOptions],
  );

  return {
    queryResult,
    allActiveQuery,
    allQuery,
    createMutation,
    updateMutation,
    removeMutation,
    activateMutation,
    deactivateMutation,
    invalidateQuery,
    providerTypeOptions,
    systemProviderTypeOptions,
  };
};
